import { useMemo, useState } from "react"

import { Input } from "./input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select"

export type SearchableSelectOption = {
  value: string
  label: string
}

interface SearchableSelectProps {
  value: string
  onValueChange: (value: string) => void
  options: SearchableSelectOption[]
  placeholder?: string
  searchPlaceholder?: string
  disabled?: boolean
  className?: string
}

// Radix Select with a filter Input pinned inside SelectContent. stopPropagation on the
// input wrapper keeps Radix's built-in typeahead-select-item behavior from stealing
// keystrokes/clicks meant for the search box.
export function SearchableSelect({
  value,
  onValueChange,
  options,
  placeholder,
  searchPlaceholder = "Search...",
  disabled,
  className,
}: SearchableSelectProps) {
  const [searchTerm, setSearchTerm] = useState("")

  const filteredOptions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    if (!term) return options
    return options.filter(
      (option) => option.label.toLowerCase().includes(term) || option.value.toLowerCase().includes(term)
    )
  }, [options, searchTerm])

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSearchTerm("")
    }
  }

  return (
    <Select value={value} onValueChange={onValueChange} onOpenChange={handleOpenChange} disabled={disabled}>
      <SelectTrigger className={className}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <div
          className="border-b border-gray-100 p-2"
          onKeyDown={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          <Input
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder={searchPlaceholder}
            className="h-8 border-gray-200"
            autoFocus
          />
        </div>
        {filteredOptions.length === 0 ? (
          <SelectItem value="_no_results" disabled>
            No results
          </SelectItem>
        ) : (
          filteredOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))
        )}
      </SelectContent>
    </Select>
  )
}
